import { forwardRef, type HTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import { useTimerToEnd } from '@/common/hooks/timer-to-end';
import { milisecondsToTime } from '@/common/lib/timer';
import { TextXSmall } from '@/components/Typography/Typography';

import css from './styles.module.scss';

interface LotteryTimerProps extends HTMLAttributes<HTMLDivElement> {
  endTime: string;
  callback?: () => void;
}

const LotteryTimer = forwardRef<HTMLDivElement, LotteryTimerProps>(
  ({ endTime, callback, ...props }, ref) => {
    const { t } = useTranslation();
    const secondsToEnd = useTimerToEnd(endTime, callback);

    const timeObj = milisecondsToTime((secondsToEnd || 0) * 1000);

    const labels = {
      withDays: [
        {
          entity: 'days',
          label: 'days',
        },
        {
          entity: '',
          label: '',
        },
        {
          entity: 'hours',
          label: 'hrs',
        },
        {
          entity: '',
          label: '',
        },
        {
          entity: 'minutes',
          label: 'min',
        },
      ],
      withSeconds: [
        {
          entity: 'hours',
          label: 'hrs',
        },
        {
          entity: '',
          label: '',
        },
        {
          entity: 'minutes',
          label: 'min',
        },
        {
          entity: '',
          label: '',
        },
        {
          entity: 'seconds',
          label: 'sec',
        },
      ],
    };

    const list =
      (secondsToEnd || 0) < 86400 ? labels.withSeconds : labels.withDays;

    return (
      <div
        ref={ref}
        {...props}
        className={cn(css.lotteryTimer, props.className)}
      >
        {list.map(({ entity, label }, index) => {
          if (label && entity) {
            const value = timeObj[entity as keyof typeof timeObj];

            return (
              <div key={index} className="flex flex-col items-center gap-[2px]">
                <div className="flex gap-[4px] h-[40px]">
                  <div className="h-full text-[24px] font-extrabold leading-none w-[33px] flex items-center justify-center bg-white/10 rounded-lg">
                    {value[0]}
                  </div>
                  <div className="h-full text-[24px] font-extrabold leading-none w-[33px] flex items-center justify-center bg-white/10 rounded-lg">
                    {value[1]}
                  </div>
                </div>
                <TextXSmall className="!text-white/60 !leading-1.2">
                  {t(label)}
                </TextXSmall>
              </div>
            );
          } else {
            return (
              <span
                key={index}
                className="text-[20px] mt-[10px] font-bold leading-none"
              >
                :
              </span>
            );
          }
        })}
      </div>
    );
  },
);

export { LotteryTimer };
